import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  appendQueueItems,
  nextQueueIndex,
  previousQueueIndex,
  removeQueueItems,
  setQueueShuffle,
  startQueue,
  type QueueModel,
  type QueuePlaybackContext,
} from './player-state.js';
import {
  clearPlaybackSession,
  readPlaybackSession,
  shouldPersistAsPlaying,
  writePlaybackSession,
} from './player-persistence.js';

export type PlaybackStatus =
  'idle' | 'loading' | 'waiting' | 'playing' | 'paused' | 'ended' | 'source-expired' | 'failed';

type PlaybackIdentity = {
  id: string;
  title: string;
  artist: string | null;
  album: string | null;
  detailHref: string;
  composerProjectId?: string;
};

export type PlaybackItem = PlaybackIdentity & {
  sourcePreference: 'owned' | 'preview' | 'direct';
  libraryId?: string;
  importId?: string;
  directUrl?: string;
};

export type PlaybackSource = {
  url: string;
  label: string;
  contentType: string | null;
  expiresAt: string | null;
};

export type PlaybackSourceReason = 'initial' | 'expired';

export type ResolvePlaybackSource = (
  item: PlaybackItem,
  context: {
    reason: PlaybackSourceReason;
    previous: PlaybackSource | null;
  },
) => Promise<PlaybackSource>;

export type PlayerState = {
  item: PlaybackItem | null;
  source: PlaybackSource | null;
  status: PlaybackStatus;
  currentTime: number;
  duration: number;
  error: string | null;
  queue: readonly PlaybackItem[];
  queueIndex: number | null;
  canonicalQueue: readonly PlaybackItem[];
  playbackContext: QueuePlaybackContext | null;
  shuffle: boolean;
};

export type PlayerController = PlayerState & {
  play: (item: PlaybackItem) => Promise<void>;
  playQueue: (
    items: readonly PlaybackItem[],
    startIndex: number,
    context: QueuePlaybackContext,
  ) => Promise<void>;
  appendQueue: (contextId: string, items: readonly PlaybackItem[]) => void;
  toggle: () => Promise<void>;
  toggleShuffle: () => void;
  retry: () => Promise<void>;
  previous: () => Promise<void>;
  next: () => Promise<void>;
  seek: (seconds: number) => void;
  close: () => void;
  removeTracks: (trackIds: readonly string[]) => void;
  removeProject: (projectId: string) => void;
};

export type PlaybackTransportController = Pick<
  PlayerController,
  | 'appendQueue'
  | 'close'
  | 'item'
  | 'play'
  | 'playQueue'
  | 'removeProject'
  | 'removeTracks'
  | 'retry'
  | 'shuffle'
  | 'status'
  | 'toggle'
  | 'toggleShuffle'
>;

const initialState: PlayerState = {
  item: null,
  source: null,
  status: 'idle',
  currentTime: 0,
  duration: 0,
  error: null,
  queue: [],
  queueIndex: null,
  canonicalQueue: [],
  playbackContext: null,
  shuffle: false,
};

const PlayerContext = createContext<PlayerController | null>(null);
const PlaybackTransportContext = createContext<PlaybackTransportController | null>(null);

function finiteTime(value: number): number {
  return Number.isFinite(value) && value >= 0 ? value : 0;
}

function sourceHasExpired(source: PlaybackSource | null): boolean {
  if (!source?.expiresAt) return false;
  const expiresAt = Date.parse(source.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt <= Date.now();
}

function setMediaSessionAction(
  action: MediaSessionAction,
  handler: MediaSessionActionHandler | null,
) {
  try {
    navigator.mediaSession.setActionHandler(action, handler);
  } catch {
    // Media Session implementations may expose only a subset of actions.
  }
}

export function PlayerProvider({
  children,
  persistenceScope = null,
  resolveSource,
}: {
  children: ReactNode;
  persistenceScope?: string | null;
  resolveSource: ResolvePlaybackSource;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const requestRef = useRef(0);
  const intendedPlaybackRef = useRef<'pause' | 'play'>('pause');
  const restorePositionRef = useRef(0);
  const hydratedScopeRef = useRef<string | null>(null);
  const advanceRef = useRef<() => void>(() => undefined);
  const stateRef = useRef<PlayerState>(initialState);
  const [state, setState] = useState<PlayerState>(initialState);

  const update = useCallback((patch: Partial<PlayerState>) => {
    setState((current) => {
      const next = { ...current, ...patch };
      stateRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'metadata';
    audioRef.current = audio;

    const onLoadStart = () => update({ status: 'loading', error: null });
    const onLoadedMetadata = () => {
      const requestedPosition = restorePositionRef.current;
      if (requestedPosition > 0) {
        audio.currentTime = Math.min(requestedPosition, finiteTime(audio.duration));
      }
      restorePositionRef.current = 0;
      update({
        currentTime: finiteTime(audio.currentTime),
        duration: finiteTime(audio.duration),
        ...(intendedPlaybackRef.current === 'pause' ? { status: 'paused' as const } : {}),
      });
    };
    const onWaiting = () => {
      if (!audio.paused) update({ status: 'waiting' });
    };
    const onPlaying = () => update({ status: 'playing', error: null });
    const onPause = () => {
      if (!audio.ended && stateRef.current.item && stateRef.current.status !== 'loading') {
        update({ status: 'paused' });
      }
    };
    const onEnded = () => {
      update({ status: 'ended', currentTime: finiteTime(audio.duration) });
      advanceRef.current();
    };
    const onTimeUpdate = () => update({ currentTime: finiteTime(audio.currentTime) });
    const onDurationChange = () => update({ duration: finiteTime(audio.duration) });
    const onError = () => {
      const expired = sourceHasExpired(stateRef.current.source);
      update({
        status: expired ? 'source-expired' : 'failed',
        error: expired
          ? 'The playback source expired.'
          : 'This track could not be played from the current source.',
      });
    };

    audio.addEventListener('loadstart', onLoadStart);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('error', onError);

    return () => {
      requestRef.current += 1;
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      audio.removeEventListener('loadstart', onLoadStart);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('error', onError);
      audioRef.current = null;
    };
  }, [update]);

  const loadItem = useCallback(
    async (
      item: PlaybackItem,
      reason: PlaybackSourceReason,
      options: { play: boolean; position?: number; tolerateBlockedPlayback?: boolean },
    ) => {
      const audio = audioRef.current;
      if (!audio) return;
      const request = ++requestRef.current;
      const previous = stateRef.current.source;

      intendedPlaybackRef.current = options.play ? 'play' : 'pause';
      restorePositionRef.current = finiteTime(options.position ?? 0);
      audio.pause();
      update({
        item,
        status: 'loading',
        currentTime: reason === 'initial' ? 0 : stateRef.current.currentTime,
        duration: reason === 'initial' ? 0 : stateRef.current.duration,
        error: null,
      });

      try {
        const source = await resolveSource(item, { reason, previous });
        if (request !== requestRef.current) return;
        const resumeAt =
          options.position ?? (reason === 'expired' ? stateRef.current.currentTime : 0);
        restorePositionRef.current = finiteTime(resumeAt);
        audio.src = source.url;
        update({ source });
        if (options.play) {
          try {
            await audio.play();
          } catch (cause) {
            if (request !== requestRef.current) return;
            if (
              options.tolerateBlockedPlayback &&
              cause instanceof DOMException &&
              cause.name === 'NotAllowedError'
            ) {
              intendedPlaybackRef.current = 'pause';
              update({ status: 'paused', error: null });
              return;
            }
            throw cause;
          }
        } else {
          audio.load();
        }
      } catch (cause) {
        if (request !== requestRef.current) return;
        update({
          status: sourceHasExpired(previous) ? 'source-expired' : 'failed',
          error: cause instanceof Error ? cause.message : 'Playback could not be started.',
        });
      }
    },
    [resolveSource, update],
  );

  const play = useCallback(
    async (item: PlaybackItem) => {
      const context: QueuePlaybackContext = {
        kind: 'single',
        id: `track:${item.id}`,
        label: item.title,
      };
      update({
        canonicalQueue: [item],
        playbackContext: context,
        queue: [],
        queueIndex: null,
      });
      await loadItem(item, 'initial', { play: true });
    },
    [loadItem, update],
  );

  const playQueue = useCallback(
    async (items: readonly PlaybackItem[], startIndex: number, context: QueuePlaybackContext) => {
      const model = startQueue(items, startIndex, context, stateRef.current.shuffle);
      const item = model?.items[model.index ?? -1];
      if (!model || !item) return;
      update({
        canonicalQueue: model.canonical,
        playbackContext: model.context,
        queue: model.items,
        queueIndex: model.index,
      });
      await loadItem(item, 'initial', { play: true });
    },
    [loadItem, update],
  );

  const appendQueue = useCallback(
    (contextId: string, items: readonly PlaybackItem[]) => {
      const current = stateRef.current;
      const model = appendQueueItems(
        {
          canonical: current.canonicalQueue,
          context: current.playbackContext,
          index: current.queueIndex,
          items: current.queue,
          shuffle: current.shuffle,
        },
        contextId,
        items,
      );
      update({ canonicalQueue: model.canonical, queue: model.items });
    },
    [update],
  );

  const retry = useCallback(async () => {
    const item = stateRef.current.item;
    if (!item) return;
    await loadItem(item, 'expired', { play: true });
  }, [loadItem]);

  const toggle = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !stateRef.current.item) return;
    if (stateRef.current.status === 'source-expired' || sourceHasExpired(stateRef.current.source)) {
      await retry();
      return;
    }
    if (audio.paused) {
      try {
        intendedPlaybackRef.current = 'play';
        await audio.play();
      } catch (cause) {
        update({
          status: 'failed',
          error: cause instanceof Error ? cause.message : 'Playback could not be resumed.',
        });
      }
    } else {
      intendedPlaybackRef.current = 'pause';
      audio.pause();
    }
  }, [retry, update]);

  const toggleShuffle = useCallback(() => {
    const current = stateRef.current;
    const model = setQueueShuffle(
      {
        canonical: current.canonicalQueue,
        context: current.playbackContext,
        index: current.queueIndex,
        items: current.queue,
        shuffle: current.shuffle,
      },
      !current.shuffle,
    );
    update({ queue: model.items, queueIndex: model.index, shuffle: model.shuffle });
  }, [update]);

  const next = useCallback(async () => {
    const { queue, queueIndex } = stateRef.current;
    const nextIndex = nextQueueIndex(queue.length, queueIndex);
    if (nextIndex === null) return;
    const item = queue[nextIndex];
    if (!item) return;
    update({ queueIndex: nextIndex });
    await loadItem(item, 'initial', { play: true });
  }, [loadItem, update]);

  const previous = useCallback(async () => {
    if (stateRef.current.currentTime > 5) {
      const audio = audioRef.current;
      if (audio) audio.currentTime = 0;
      return;
    }
    const { queue, queueIndex } = stateRef.current;
    const previousIndex = previousQueueIndex(queueIndex);
    if (previousIndex === null) return;
    const item = queue[previousIndex];
    if (!item) return;
    update({ queueIndex: previousIndex });
    await loadItem(item, 'initial', { play: true });
  }, [loadItem, update]);

  advanceRef.current = () => {
    const { queue, queueIndex } = stateRef.current;
    if (nextQueueIndex(queue.length, queueIndex) !== null) void next();
  };

  const seek = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const maximum = finiteTime(audio.duration);
    audio.currentTime = Math.max(0, Math.min(finiteTime(seconds), maximum));
  }, []);

  const close = useCallback(() => {
    requestRef.current += 1;
    intendedPlaybackRef.current = 'pause';
    restorePositionRef.current = 0;
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    stateRef.current = initialState;
    setState(initialState);
    if (persistenceScope) clearPlaybackSession(persistenceScope);
  }, [persistenceScope]);

  const removeTracks = useCallback(
    (trackIds: readonly string[]) => {
      const current = stateRef.current;
      const model: QueueModel<PlaybackItem> = {
        canonical: current.canonicalQueue,
        context: current.playbackContext,
        index: current.queueIndex,
        items: current.queue,
        shuffle: current.shuffle,
      };
      const disposition = removeQueueItems(model, current.item, new Set(trackIds));
      if (disposition.kind === 'close') {
        close();
        return;
      }
      if (disposition.kind === 'updated') {
        update({
          canonicalQueue: disposition.model.canonical,
          queue: disposition.model.items,
          queueIndex: disposition.model.index,
        });
        return;
      }
      if (disposition.kind === 'current') {
        const shouldPlay = ['loading', 'playing', 'waiting'].includes(current.status);
        update({
          canonicalQueue: disposition.model.canonical,
          playbackContext: disposition.model.context,
          queue: disposition.model.items,
          queueIndex: disposition.model.index,
        });
        void loadItem(disposition.item, 'initial', { play: shouldPlay });
      }
    },
    [close, loadItem, update],
  );

  const removeProject = useCallback(
    (projectId: string) => {
      const current = stateRef.current;
      const removedIds = current.canonicalQueue
        .filter((item) => item.composerProjectId === projectId)
        .map((item) => item.id);
      if (current.item?.composerProjectId === projectId && !removedIds.includes(current.item.id)) {
        removedIds.push(current.item.id);
      }
      removeTracks(removedIds);
    },
    [removeTracks],
  );

  const persistCurrentSession = useCallback(() => {
    if (!persistenceScope || hydratedScopeRef.current !== persistenceScope) return;
    const current = stateRef.current;
    if (!current.item) return;
    writePlaybackSession(persistenceScope, {
      item: current.item,
      model: {
        canonical: current.canonicalQueue,
        context: current.playbackContext,
        index: current.queueIndex,
        items: current.queue,
        shuffle: current.shuffle,
      },
      position: current.currentTime,
      wasPlaying: shouldPersistAsPlaying(current.status),
    });
  }, [persistenceScope]);

  useEffect(() => {
    if (!persistenceScope || hydratedScopeRef.current === persistenceScope) return;
    requestRef.current += 1;
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    stateRef.current = initialState;
    setState(initialState);
    hydratedScopeRef.current = persistenceScope;
    const saved = readPlaybackSession(persistenceScope);
    if (!saved) return;
    const restored: PlayerState = {
      ...initialState,
      item: saved.item,
      canonicalQueue: saved.model.canonical,
      playbackContext: saved.model.context,
      queue: saved.model.items,
      queueIndex: saved.model.index,
      shuffle: saved.model.shuffle,
      currentTime: saved.position,
      status: 'loading',
    };
    stateRef.current = restored;
    setState(restored);
    void loadItem(saved.item, 'initial', {
      play: saved.wasPlaying,
      position: saved.position,
      tolerateBlockedPlayback: true,
    });
  }, [loadItem, persistenceScope]);

  const hasPlaybackItem = state.item !== null;
  useEffect(() => {
    if (!hasPlaybackItem || !persistenceScope || hydratedScopeRef.current !== persistenceScope)
      return;
    persistCurrentSession();
    const interval = window.setInterval(persistCurrentSession, 1_000);
    return () => window.clearInterval(interval);
  }, [hasPlaybackItem, persistenceScope, persistCurrentSession]);

  useEffect(() => {
    const onPageHide = () => persistCurrentSession();
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [persistCurrentSession]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const item = state.item;
    navigator.mediaSession.metadata = item
      ? new MediaMetadata({
          title: item.title,
          ...(item.artist ? { artist: item.artist } : {}),
          ...(item.album ? { album: item.album } : {}),
        })
      : null;
    setMediaSessionAction('play', () => void toggle());
    setMediaSessionAction('pause', () => void toggle());
    setMediaSessionAction('previoustrack', () => void previous());
    setMediaSessionAction('nexttrack', () => void next());
    setMediaSessionAction('seekto', (details) => {
      if (details.seekTime !== undefined) seek(details.seekTime);
    });
    return () => {
      navigator.mediaSession.metadata = null;
      setMediaSessionAction('play', null);
      setMediaSessionAction('pause', null);
      setMediaSessionAction('previoustrack', null);
      setMediaSessionAction('nexttrack', null);
      setMediaSessionAction('seekto', null);
    };
  }, [next, previous, seek, state.item, toggle]);

  useEffect(() => {
    if (!('mediaSession' in navigator) || !state.item || state.duration <= 0) return;
    try {
      navigator.mediaSession.setPositionState({
        duration: state.duration,
        playbackRate: 1,
        position: Math.min(state.currentTime, state.duration),
      });
    } catch {
      // Some platforms expose Media Session without accepting position state.
    }
  }, [state.currentTime, state.duration, state.item]);

  const value = useMemo<PlayerController>(
    () => ({
      ...state,
      play,
      playQueue,
      appendQueue,
      toggle,
      toggleShuffle,
      retry,
      previous,
      next,
      seek,
      close,
      removeTracks,
      removeProject,
    }),
    [
      appendQueue,
      close,
      next,
      play,
      playQueue,
      previous,
      removeProject,
      removeTracks,
      retry,
      seek,
      state,
      toggle,
      toggleShuffle,
    ],
  );
  const transport = useMemo<PlaybackTransportController>(
    () => ({
      appendQueue,
      close,
      item: state.item,
      play,
      playQueue,
      removeProject,
      removeTracks,
      retry,
      shuffle: state.shuffle,
      status: state.status,
      toggle,
      toggleShuffle,
    }),
    [
      appendQueue,
      close,
      play,
      playQueue,
      removeProject,
      removeTracks,
      retry,
      state.item,
      state.shuffle,
      state.status,
      toggle,
      toggleShuffle,
    ],
  );

  return (
    <PlayerContext.Provider value={value}>
      <PlaybackTransportContext.Provider value={transport}>
        {children}
      </PlaybackTransportContext.Provider>
    </PlayerContext.Provider>
  );
}

export function usePlayer(): PlayerController {
  const player = useContext(PlayerContext);
  if (!player) throw new Error('usePlayer must be used inside PlayerProvider.');
  return player;
}

export function usePlaybackTransport(): PlaybackTransportController {
  const transport = useContext(PlaybackTransportContext);
  if (!transport) throw new Error('usePlaybackTransport must be used inside PlayerProvider.');
  return transport;
}
